import { useState } from 'react';
import { FileArchive, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  downloadQuestionSetExtraction,
  type QuestionSetModule,
} from '@/lib/questionSetExtractionApi';
import {
  DEFAULT_CONTEXTS_PER_SECTION,
  MAX_CONTEXTS_PER_SECTION,
  MIN_CONTEXTS_PER_SECTION,
  QUESTION_SET_MODULES,
  isValidContextsPerSection,
} from '@/components/admin/questionSetExtractionConfig';

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

interface QuestionSetExtractionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function QuestionSetExtractionDialog({
  open,
  onOpenChange,
}: Readonly<QuestionSetExtractionDialogProps>) {
  const [level, setLevel] = useState('');
  const [selectedModules, setSelectedModules] = useState<Set<QuestionSetModule>>(
    () => new Set(QUESTION_SET_MODULES.map((module) => module.value)),
  );
  const [contextsPerSection, setContextsPerSection] = useState(
    DEFAULT_CONTEXTS_PER_SECTION,
  );
  const [isExtracting, setIsExtracting] = useState(false);

  const toggleModule = (module: QuestionSetModule) => {
    setSelectedModules((previous) => {
      const next = new Set(previous);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }
      return next;
    });
  };

  const handleExtract = async () => {
    if (!level) {
      toast.error('Select a level');
      return;
    }
    if (selectedModules.size === 0) {
      toast.error('Select at least one module');
      return;
    }
    if (!isValidContextsPerSection(contextsPerSection)) {
      toast.error('Contexts per section must be between 1 and 100');
      return;
    }

    setIsExtracting(true);
    try {
      await downloadQuestionSetExtraction({
        level,
        modules: QUESTION_SET_MODULES
          .map((module) => module.value)
          .filter((module) => selectedModules.has(module)),
        contextsPerSection,
      });
      toast.success('Question set archive downloaded');
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to extract question set',
      );
    } finally {
      setIsExtracting(false);
    }
  };

  const canExtract =
    Boolean(level) &&
    selectedModules.size > 0 &&
    isValidContextsPerSection(contextsPerSection) &&
    !isExtracting;

  return (
    <Dialog open={open} onOpenChange={isExtracting ? undefined : onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-ui flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-primary" />
            Extract question set
          </DialogTitle>
          <DialogDescription className="font-ui">
            Create one offline HTML ZIP with a random context sample for every
            section in each selected module.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="space-y-2">
            <Label htmlFor="question-set-level" className="font-ui">
              Level
            </Label>
            <Select value={level} onValueChange={setLevel} disabled={isExtracting}>
              <SelectTrigger id="question-set-level" className="font-ui">
                <SelectValue placeholder="Select a CEFR level" />
              </SelectTrigger>
              <SelectContent>
                {LEVELS.map((item) => (
                  <SelectItem key={item} value={item} className="font-ui">
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <fieldset className="space-y-3">
            <legend className="font-ui text-sm font-medium text-foreground">
              Modules
            </legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {QUESTION_SET_MODULES.map((module) => (
                <div
                  key={module.value}
                  className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3"
                >
                  <Checkbox
                    id={`question-set-module-${module.value}`}
                    checked={selectedModules.has(module.value)}
                    onCheckedChange={() => toggleModule(module.value)}
                    disabled={isExtracting}
                  />
                  <Label
                    htmlFor={`question-set-module-${module.value}`}
                    className="font-ui cursor-pointer"
                  >
                    {module.label}
                  </Label>
                </div>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="contexts-per-section" className="font-ui">
              Contexts per section
            </Label>
            <Input
              id="contexts-per-section"
              type="number"
              min={MIN_CONTEXTS_PER_SECTION}
              max={MAX_CONTEXTS_PER_SECTION}
              step={1}
              value={contextsPerSection}
              onChange={(event) =>
                setContextsPerSection(Number(event.target.value))
              }
              disabled={isExtracting}
              className="font-ui"
            />
            <p className="font-ui text-xs text-muted-foreground">
              The system samples this many distinct contexts independently for
              every available section. Sections with fewer contexts export all
              available matches.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="font-ui text-sm text-foreground">
              The archive includes active matching questions from all exam
              providers, answer keys, explanations, transcripts, and available
              context media.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExtracting}
          >
            <span className="font-ui">Cancel</span>
          </Button>
          <Button type="button" onClick={handleExtract} disabled={!canExtract}>
            {isExtracting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileArchive className="mr-2 h-4 w-4" />
            )}
            <span className="font-ui">
              {isExtracting ? 'Preparing archive…' : 'Download HTML ZIP'}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
